/**
 * HomePage – with purposeful, restrained animations
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TrackSummary } from '../types';
import { useAuth } from '../context/AuthContext';
import { useInView, usePageLoadAnimation } from '../hooks/useAnimation';
import { TrackCard } from '../components/shared/TrackCard';
import { API_BASE_URL } from '../config';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredTrack, setHoveredTrack] = useState<string | null>(null);
  
  const isLoaded = usePageLoadAnimation();
  const [statsRef, statsInView] = useInView<HTMLDivElement>();
  const [libraryRef, libraryInView] = useInView<HTMLDivElement>();
  const [howItWorksRef, howItWorksInView] = useInView<HTMLDivElement>();

  const fetchTracks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tracks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as TrackSummary[];
      setTracks(data.slice(0, 6)); // Only show first 6 tracks on homepage
    } catch (e) {
      console.error('Failed to load tracks:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTracks();
  }, [fetchTracks]);

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0 bg-studio-950">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(245,166,35,0.08)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(224,122,95,0.06)_0%,transparent_50%)]" />
        </div>

        <div className="relative w-full px-4 sm:px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left: Typography with staggered entrance */}
              <div className={`order-2 lg:order-1 transition-all duration-700 text-center lg:text-left ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <div 
                  className="flex items-center justify-center lg:justify-start gap-3 mb-6 lg:mb-8"
                  style={{ transitionDelay: '100ms' }}
                >
                  <div className="w-12 h-[2px] bg-amber-500 hidden lg:block" />
                  <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
                    For Music Students
                  </span>
                </div>
                
                <h1 
                  className="font-display text-[clamp(2.5rem,10vw,8rem)] font-bold text-stone-100 leading-[0.9] mb-6 lg:mb-8"
                  style={{ 
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 150ms'
                  }}
                >
                  Master
                  <br />
                  <span className="text-amber-500 italic">timing.</span>
                </h1>
                
                <p 
                  className="text-stone-400 text-lg lg:text-xl leading-relaxed mb-8 lg:mb-10 max-w-md mx-auto lg:mx-0"
                  style={{ 
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 250ms'
                  }}
                >
                  A precision tool for developing your internal metronome. 
                  Practice with curated exercises and receive detailed 
                  feedback on your rhythmic accuracy.
                </p>

                <div 
                  className="flex flex-wrap items-center gap-4 justify-center lg:justify-start"
                  style={{ 
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 350ms'
                  }}
                >
                  <button 
                    type="button" 
                    onClick={() => navigate('/learn')} 
                    className="btn-primary"
                  >
                    Start Learning
                  </button>
                  
                  {user?.is_admin && (
                    <button type="button" onClick={() => navigate('/admin')} className="btn-secondary">
                      Studio
                    </button>
                  )}
                </div>

                {/* Stats with animated counters */}
                <div 
                  ref={statsRef}
                  className={`flex flex-wrap justify-center lg:justify-start gap-8 lg:gap-12 mt-12 lg:mt-16 pt-6 lg:pt-8 border-t border-studio-800 transition-all duration-700 ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                >
                  <StatCounter value={6} label="Practice Lanes" inView={statsInView} delay={0} />
                  <StatCounter value={50} suffix="ms" label="Perfect Window" inView={statsInView} delay={100} />
                  <StatCounter value={4} label="Difficulty Levels" inView={statsInView} delay={200} />
                </div>
              </div>

              {/* Right: Animated visual */}
              <HeroVisual isLoaded={isLoaded} />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tracks Preview Section */}
      <section ref={libraryRef} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 transition-all duration-700 ${libraryInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div>
              <span className="text-coral-400 text-sm font-semibold tracking-widest uppercase mb-3 block">
                Featured Exercises
              </span>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-stone-100">
                Popular <span className="italic text-stone-400">tracks.</span>
              </h2>
            </div>

            <Link 
              to="/learn"
              className="group flex items-center gap-2 text-amber-500 hover:text-amber-400 font-semibold transition-colors"
            >
              View All Exercises
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>

          {/* Track Grid - Limited to 6 tracks */}
          {loading ? (
            <div className="flex justify-center py-24">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-stone-500">Loading exercises...</p>
              </div>
            </div>
          ) : tracks.length === 0 ? (
            <div className="text-center py-24 border-2 border-dashed border-studio-800 rounded-3xl">
              <p className="text-stone-500 text-lg">No exercises available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {tracks.map((track, index) => (
                <TrackCard
                  key={track.id}
                  track={track}
                  onPlay={() => user ? navigate(`/game/${track.id}`) : navigate('/login')}
                  isLoggedIn={!!user}
                  isHovered={hoveredTrack === track.id}
                  onHover={() => setHoveredTrack(track.id)}
                  onLeave={() => setHoveredTrack(null)}
                  index={index}
                  isVisible={libraryInView}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section ref={howItWorksRef} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 bg-studio-950 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-500/5 to-transparent" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className={`text-center mb-12 lg:mb-20 transition-all duration-700 ${howItWorksInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-4 block">
              How It Works
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100">
              Three steps to <span className="italic text-amber-500">mastery.</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 lg:gap-12">
            {[
              { num: '01', title: 'Select Exercise', desc: 'Choose from exercises organized by difficulty, from fundamental patterns to complex professional rhythms.', color: '#f5a623' },
              { num: '02', title: 'Feel the Beat', desc: 'Six lanes correspond to keys S, D, F, J, K, L. Strike each note as it crosses the guide line.', color: '#e07a5f' },
              { num: '03', title: 'Analyze & Improve', desc: 'Review detailed accuracy reports, identify timing tendencies, and track your improvement.', color: '#81b29a' },
            ].map((step, i) => (
              <StepCard key={step.num} step={step} index={i} isVisible={howItWorksInView} />
            ))}
          </div>
        </div>
      </section>

      {/* Rhythm Lab Section */}
      <RhythmLabSection />

      {/* Community & Progress Section */}
      <CommunitySection user={user} />

      {/* Keyboard Reference */}
      <KeyboardSection />
    </div>
  );
};

// ============================================
// Sub-components with animations
// ============================================

function StatCounter({ value, suffix = '', label, inView, delay }: { value: number; suffix?: string; label: string; inView: boolean; delay: number }) {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!inView) return;
    
    const timer = setTimeout(() => {
      const duration = 1000;
      const startTime = performance.now();
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeProgress * value));
        
        if (progress < 1) requestAnimationFrame(animate);
        else setCount(value);
      };
      
      requestAnimationFrame(animate);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [inView, value, delay]);

  return (
    <div>
      <p className="font-display text-4xl font-bold text-stone-100">{count}{suffix}</p>
      <p className="text-stone-500 text-sm mt-1">{label}</p>
    </div>
  );
}

function HeroVisual({ isLoaded }: { isLoaded: boolean }) {
  return (
    <div 
      className="order-1 lg:order-2 flex justify-center lg:justify-end"
      style={{ 
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? 'scale(1)' : 'scale(0.9)',
        transition: 'all 0.9s cubic-bezier(0.22, 1, 0.36, 1) 200ms'
      }}
    >
      <div className="relative">
        {/* Decorative rings with pulse */}
        <div className="absolute -inset-20 opacity-20">
          <div className="w-full h-full rounded-full border border-amber-500/30 animate-pulse" style={{ animationDuration: '4s' }} />
        </div>
        <div className="absolute -inset-32 opacity-10">
          <div className="w-full h-full rounded-full border border-coral-400/30" />
        </div>
        
        {/* Main visual */}
        <div className="relative w-72 h-72 lg:w-96 lg:h-96">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            {[...Array(12)].map((_, i) => (
              <line
                key={`line-${i}`}
                x1="100"
                y1="20"
                x2="100"
                y2="35"
                stroke="#f5a623"
                strokeWidth="2"
                opacity={0.3 + (i % 3) * 0.2}
                transform={`rotate(${i * 30} 100 100)`}
              />
            ))}
            {[...Array(8)].map((_, i) => {
              const height = 20 + Math.sin(i * 0.8) * 15;
              return (
                <rect
                  key={`bar-${i}`}
                  x="96"
                  y={100 - height}
                  width="8"
                  height={height * 2}
                  rx="4"
                  fill={i % 2 === 0 ? '#f5a623' : '#e07a5f'}
                  opacity={0.6 + Math.sin(i) * 0.3}
                  transform={`rotate(${i * 45} 100 100)`}
                />
              );
            })}
            <circle cx="100" cy="100" r="25" fill="#0a0a0b" stroke="#f5a623" strokeWidth="2" />
            <circle cx="100" cy="100" r="8" fill="#f5a623" className="animate-pulse-subtle" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function StepCard({ step, index, isVisible }: { step: any; index: number; isVisible: boolean }) {
  return (
    <div 
      className="relative group"
      style={{ 
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${200 + index * 150}ms`
      }}
    >
      <span 
        className="font-display text-[8rem] font-bold leading-none absolute -top-8 -left-4 opacity-10 transition-opacity duration-500 group-hover:opacity-20"
        style={{ color: step.color }}
      >
        {step.num}
      </span>
      <div className="relative pt-16">
        <div className="w-16 h-1 mb-6 transition-all duration-300 group-hover:w-24" style={{ backgroundColor: step.color }} />
        <h3 className="text-2xl font-bold text-stone-100 mb-4">{step.title}</h3>
        <p className="text-stone-500 leading-relaxed">{step.desc}</p>
      </div>
    </div>
  );
}

// ============================================
// Rhythm Lab Section — science of rhythm
// ============================================

function RhythmLabSection() {
  const [ref, isInView] = useInView<HTMLElement>();

  const concepts = [
    {
      title: 'Tempo & BPM',
      body: 'Beats Per Minute defines the pulse of a track. Most popular music sits between 60–180 BPM — your heart rate at rest is ~60 BPM.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
        </svg>
      ),
      color: '#f5a623',
      delay: 0,
    },
    {
      title: 'Syncopation',
      body: 'Placing emphasis on normally weak beats creates groove. Funk, jazz, and Latin music thrive on unexpected accents between the downbeats.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      ),
      color: '#e07a5f',
      delay: 100,
    },
    {
      title: 'Polyrhythm',
      body: 'Two or more rhythmic patterns played simultaneously. A 3-against-2 pattern — three evenly spaced notes over two beats — is a classic example.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h6m-6 4h6m4-4h6m-6 4h6" />
        </svg>
      ),
      color: '#81b29a',
      delay: 200,
    },
    {
      title: 'Latency & Feel',
      body: 'Expert musicians play slightly behind or ahead of the beat intentionally. This micro-timing — called "feel" — defines musical style.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: '#d4a574',
      delay: 300,
    },
  ];

  return (
    <section ref={ref} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 relative overflow-hidden">
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(245,166,35,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(245,166,35,0.03) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div
          className="mb-14 lg:mb-20"
          style={{
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <span className="text-coral-400 text-sm font-semibold tracking-widest uppercase mb-3 block">
            The Science of Rhythm
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100">
              Rhythm <span className="italic text-coral-400">lab.</span>
            </h2>
            <p className="text-stone-500 max-w-sm text-sm lg:text-base lg:text-right">
              Four concepts that separate good timing from great musicianship.
            </p>
          </div>
        </div>

        {/* Concept cards — horizontal strip layout */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {concepts.map((c) => (
            <div
              key={c.title}
              className="group relative rounded-2xl p-6 overflow-hidden"
              style={{
                background: 'linear-gradient(145deg, #111111 0%, #0d0d0d 100%)',
                border: `1px solid ${c.color}20`,
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateY(0)' : 'translateY(28px)',
                transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${c.delay}ms`,
              }}
            >
              {/* Glow on hover */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
                style={{ boxShadow: `inset 0 0 40px ${c.color}10` }}
              />

              {/* Icon circle */}
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
                style={{ background: `${c.color}15`, color: c.color, border: `1px solid ${c.color}30` }}
              >
                {c.icon}
              </div>

              {/* Animated bottom bar */}
              <div
                className="absolute bottom-0 left-0 h-[2px] transition-all duration-500 group-hover:w-full"
                style={{ background: c.color, width: '30%' }}
              />

              <h3 className="text-lg font-bold text-stone-100 mb-3 transition-colors duration-300" style={{ color: undefined }}>
                {c.title}
              </h3>
              <p className="text-stone-500 text-sm leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// Community & Progress Section
// ============================================

interface CommunitySectionProps {
  user: { username: string; is_admin?: boolean } | null;
}

function CommunitySection({ user }: CommunitySectionProps) {
  const [ref, isInView] = useInView<HTMLElement>();
  const navigate = useNavigate();

  const milestones = [
    { label: 'First Perfect Hit', desc: 'Land your first note within the 50ms window.', icon: '◎', color: '#81b29a' },
    { label: '10-Note Combo', desc: 'Chain 10 consecutive good or perfect hits.', icon: '⬡', color: '#f5a623' },
    { label: 'S-Rank Run', desc: 'Finish any exercise with 95%+ accuracy.', icon: '✦', color: '#e8b86d' },
    { label: 'Speed Demon', desc: 'Complete a 160+ BPM track without missing.', icon: '◈', color: '#e07a5f' },
  ];

  return (
    <section ref={ref} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 bg-studio-950 relative overflow-hidden">
      {/* Diagonal accent stripe */}
      <div
        className="absolute -right-32 top-0 bottom-0 w-96 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, rgba(129,178,154,0.04), rgba(129,178,154,0.01))' }}
      />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: copy */}
          <div
            style={{
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'translateX(0)' : 'translateX(-24px)',
              transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <span className="text-sage-400 text-sm font-semibold tracking-widest uppercase mb-4 block" style={{ color: '#81b29a' }}>
              Progress & Community
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100 mb-6">
              Chase the <span className="italic text-sage-400" style={{ color: '#81b29a' }}>leaderboard.</span>
            </h2>
            <p className="text-stone-400 leading-relaxed mb-8 max-w-md">
              Every session builds toward a milestone. Compare your timing accuracy with other students,
              track your consistency over time, and compete on per-track leaderboards.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/leaderboard')}
                className="btn-primary"
                style={{ background: '#81b29a', color: '#0a0a0b' }}
              >
                View Leaderboard
              </button>
              {!user && (
                <button type="button" onClick={() => navigate('/login')} className="btn-secondary">
                  Sign Up Free
                </button>
              )}
            </div>
          </div>

          {/* Right: milestone cards */}
          <div className="grid grid-cols-2 gap-4">
            {milestones.map((m, i) => (
              <div
                key={m.label}
                className="group rounded-2xl p-5 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(145deg, #111 0%, #0d0d0d 100%)',
                  border: `1px solid ${m.color}20`,
                  opacity: isInView ? 1 : 0,
                  transform: isInView ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.96)',
                  transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${150 + i * 100}ms`,
                }}
              >
                {/* Corner glow */}
                <div
                  className="absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none"
                  style={{ background: m.color, filter: 'blur(24px)' }}
                />

                <span
                  className="text-2xl block mb-3 transition-transform duration-300 group-hover:scale-125 origin-left"
                  style={{ color: m.color }}
                >
                  {m.icon}
                </span>
                <p className="text-stone-100 font-semibold text-sm mb-1">{m.label}</p>
                <p className="text-stone-600 text-xs leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function KeyboardSection() {
  const [ref, isInView] = useInView<HTMLElement>();
  
  return (
    <section ref={ref} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-20">
      <div className="max-w-5xl mx-auto">
        <div 
          className="card-editorial rounded-3xl p-8 lg:p-12"
          style={{ 
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.98)',
            transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)'
          }}
        >
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
            <div className="text-center lg:text-left">
              <h3 className="font-display text-2xl lg:text-3xl font-bold text-stone-100 mb-3">Input Methods</h3>
              <p className="text-stone-500 max-w-sm text-sm lg:text-base">
                Use keyboard keys S-D-F-J-K-L on desktop, or tap the on-screen zones when playing on mobile or tablet.
              </p>
            </div>
            
            <div className="flex gap-2 lg:gap-3">
              {['S', 'D', 'F', 'J', 'K', 'L'].map((key, i) => (
                <div
                  key={key}
                  className="w-10 h-12 lg:w-14 lg:h-16 rounded-lg lg:rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110 hover:-translate-y-1"
                  style={{
                    background: i < 3 
                      ? 'linear-gradient(145deg, rgba(245,166,35,0.15), rgba(245,166,35,0.05))' 
                      : 'linear-gradient(145deg, rgba(224,122,95,0.15), rgba(224,122,95,0.05))',
                    border: `2px solid ${i < 3 ? 'rgba(245,166,35,0.4)' : 'rgba(224,122,95,0.4)'}`,
                    boxShadow: `0 8px 32px ${i < 3 ? 'rgba(245,166,35,0.15)' : 'rgba(224,122,95,0.15)'}`,
                    opacity: isInView ? 1 : 0,
                    transform: isInView ? 'translateY(0)' : 'translateY(20px)',
                    transition: `all 0.4s cubic-bezier(0.22, 1, 0.36, 1) ${400 + i * 80}ms, transform 0.2s`
                  }}
                >
                  <span className="font-mono text-base lg:text-xl font-bold" style={{ color: i < 3 ? '#f5a623' : '#e07a5f' }}>
                    {key}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
